/**
 * Reasoning Resource
 *
 * Thin 1:1 mapping to the reasoning API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  SendMessageRequest,
  SendMessageResponse,
  GenerateSessionNameRequest,
  GenerateSessionNameResponse,
  ListAgentsRequest,
  AgentListResponse,
  StopExecutionRequest,
  StopExecutionResponse,
  RedirectExecutionRequest,
  RedirectExecutionResponse,
  ValidateDSLRequest,
  ValidateDSLResponse,
} from '../types/index.js';

export class ReasoningResource {
  constructor(private http: HttpLayer) {}

  private prefix(workspaceId: string | null | undefined): string {
    return workspaceId ? `/v1/reasoning/${workspaceId}` : '/v1/reasoning';
  }

  // --- Messaging ---

  /**
   * Workspace-scoped when a workspaceId is supplied, else the workspace-less
   * sibling route.
   */
  async sendMessage(
    workspaceId: string | null | undefined,
    params: SendMessageRequest,
  ): Promise<SendMessageResponse> {
    return this.http.post(
      `${this.prefix(workspaceId)}/send-message`,
      params,
    );
  }

  /**
   * Workspace-scoped when a workspaceId is supplied, else the workspace-less
   * sibling route — a workspace-less chat's title
   * is generated on the account-scoped lane.
   */
  async generateSessionName(
    workspaceId: string | null | undefined,
    params: GenerateSessionNameRequest,
  ): Promise<GenerateSessionNameResponse> {
    return this.http.post(
      `${this.prefix(workspaceId)}/generate-session-name`,
      params,
    );
  }

  // --- Agents ---

  /**
   * List the agents and personas this account can hand work to. Workspace-scoped
   * when a workspaceId is supplied, else the workspace-less sibling route.
   */
  async listAgents(
    workspaceId: string | null | undefined,
    params: ListAgentsRequest,
  ): Promise<AgentListResponse> {
    return this.http.post(
      `${this.prefix(workspaceId)}/list-agents`,
      params,
    );
  }

  // --- Execution control ---

  /**
   * Stop work in a session. `mode: 'turn'` ends the participant's turn and its
   * foreground work and spares background work; `mode: 'all'` (the default)
   * stops the participant's whole subtree. `participant_id` defaults to the primary.
   */
  async stopExecution(
    workspaceId: string | null,
    sessionId: string,
    params: StopExecutionRequest,
  ): Promise<StopExecutionResponse> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/stop`,
      params,
    );
  }

  /**
   * Redirect a turn with new user content: the primary's live turn by
   * `workflow_id`, or a subagent by `participant_id`, whose correction runs first.
   */
  async redirectExecution(
    workspaceId: string | null,
    sessionId: string,
    params: RedirectExecutionRequest,
  ): Promise<RedirectExecutionResponse> {
    return this.http.post(
      `${this.prefix(workspaceId)}/sessions/${sessionId}/redirect`,
      params,
    );
  }

  // --- DSL validation ---

  async validateDSL(
    params: ValidateDSLRequest,
  ): Promise<ValidateDSLResponse> {
    return this.http.post('/v1/reasoning/validate-dsl', params);
  }
}
